import React, { useState } from 'react';
import { AudienceAnalysisResult } from '../types/audience';
import { ChevronDown, ChevronUp, Users, Heart, AlertTriangle, Award, Compass, Target, Zap, BarChart, Lightbulb, Download } from 'lucide-react';
import jsPDF from 'jspdf';

// Cyrillic font loader for jsPDF (loads from /public/TikTokSans.ttf)
let pdfFontLoaded = false;
const loadCyrillicFont = async (pdf: any): Promise<boolean> => {
  if (pdfFontLoaded) return true;
  try {
    const response = await fetch('/TikTokSans.ttf');
    if (!response.ok) return false;
    const fontBuffer = await response.arrayBuffer();
    const fontBytes = new Uint8Array(fontBuffer);
    let binary = '';
    for (let i = 0; i < fontBytes.length; i++) binary += String.fromCharCode(fontBytes[i]);
    const base64 = btoa(binary);
    pdf.addFileToVFS('TikTokSans.ttf', base64);
    pdf.addFont('TikTokSans.ttf', 'TikTokSans', 'normal');
    pdf.addFont('TikTokSans.ttf', 'TikTokSans', 'bold');
    pdf.addFont('TikTokSans.ttf', 'TikTokSans', 'italic');
    pdfFontLoaded = true;
    return true;
  } catch {
    return false;
  }
};

interface AudienceAnalysisResultProps {
  data: AudienceAnalysisResult;
}

// --- Helper Functions ---

// Renders primitive values or simple arrays
const renderFinalValue = (value: any): JSX.Element | null => {
  if (value === null || value === undefined || value === '') {
     return <span className="text-gray-500 italic">Нет данных</span>;
  }
  
  // Обработка встроенного форматирования (жирный, курсив, ссылки)
  const processInlineFormatting = (text: string): React.ReactNode => {
    if (!text) return null;
    const cleanedText = text.replace(/^\*\*\s*/, ''); // Keep initial cleaning
    const elements: React.ReactNode[] = [];
    let lastIndex = 0;
    const linkPattern = /\[((?:[^\[\]]|\[[^\[\]]*\])*?)\]\(((?:[^()]|\([^()]*\))*?)\)/g;
    let linkMatch;

    while ((linkMatch = linkPattern.exec(cleanedText)) !== null) {
      // Text before the link
      if (linkMatch.index > lastIndex) {
        elements.push(cleanedText.substring(lastIndex, linkMatch.index));
      }

      // Process the link - Create <a> element
      const linkText = linkMatch[1];
      const url = linkMatch[2];
      const key = `link-${linkMatch.index}-${linkText.substring(0, 5)}`;
      const linkParts = linkText.split('|').map(part => part.trim());
      const displayText = linkParts[0];
      const extraInfo = linkParts.length > 1 ? linkParts[1] : '';

      elements.push(
        <a
          key={key}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
          style={{ textDecoration: 'underline', cursor: 'pointer' }}
        >
          {displayText}
          {extraInfo && <span className="text-gray-500 dark:text-gray-400 text-sm ml-1">({extraInfo})</span>}
        </a>
      );

      lastIndex = linkPattern.lastIndex;
    }

    // Text after the last link
    if (lastIndex < cleanedText.length) {
      elements.push(cleanedText.substring(lastIndex));
    }

    // Return React elements
    if (elements.length === 0) return cleanedText; // No links found
    if (elements.length === 1 && typeof elements[0] === 'string') return elements[0]; // Only text
    return <>{elements}</>; // Return array of nodes/strings
  };

  // Обработка форматированного текста с поддержкой списков
  const formatText = (text: string): JSX.Element => {
    if (!text) return <></>;

    // Проверяем, содержит ли текст нумерованный список
    const hasNumberedList = /\d+\.\s+/.test(text);
    
    if (hasNumberedList) {
      // Пробуем разные варианты разбора нумерованного списка
      const listItems: React.ReactNode[] = [];
      let hasMatches = false;
      
      // Предварительная очистка от лишних пробелов в тексте
      const cleanedText = text.replace(/\s+/g, ' ').trim();
      
      // Вариант 0: Точное соответствие формату из скриншота
      const tryExactScreenshotFormat = () => {
        // Пример: "1. Зацепить болью: ** 'Устали от хаоса в контенте? Идеи закончились?'. 2. Представить решение: ** 'Dissai - AI-платформа, которая... (ключевое УТП - все в одном, AI, Reels)'. 3. ..."
        // Включаем опциональную группу (?:\*\*\s*)? для обнаружения ** перед цитатой
        const exactReg = /(\d+)\.\s+([^:]+):\s+(?:\*\*\s*)?(?:'|"|«)([^'"""»]+)(?:'|"|»)\.?\s*(?:\d+\.|$)/g;
        let match;
        let foundExact = false;
        exactReg.lastIndex = 0;
        
        while ((match = exactReg.exec(cleanedText)) !== null) {
          foundExact = true;
          hasMatches = true;
          const number = match[1];
          const title = match[2].trim();
          const quote = match[3].trim();
          
          listItems.push(
            <li key={`item-${number}-exact`} className="mb-1">
              <strong>{title}</strong>: '{quote}'
            </li>
          );
        }
        
        return foundExact;
      };
      
      // Вариант 1: Формат "1. Заголовок: содержимое"
      const tryHeadingFormat = () => {
        if (hasMatches) return true; // Уже нашли матчи
        
        // Добавляем опциональную группу (?:\*\*\s*)? для обнаружения ** перед содержимым
        const headingItemRegex = /(\d+)\.\s+([^:]+):\s*(?:\*\*\s*)?(.+?)(?=\s+\d+\.\s+|$)/gs;
        let match;
        headingItemRegex.lastIndex = 0;
        
        while ((match = headingItemRegex.exec(cleanedText)) !== null) {
          hasMatches = true;
          const number = match[1];
          const title = match[2].trim();
          const cleanedTitle = title.replace(/^\*\*|\*\*$|^__|__$/g, '');
          let content = match[3].trim();
          
          // Удаляем ** в начале содержимого, если они остались
          content = content.replace(/^\*\*\s*/, '');
          
          // Проверяем, если содержимое начинается с кавычек
          const quoteMatch = /^['"](.+?)['"](.*)$/.exec(content);
          if (quoteMatch) {
            const quotedText = quoteMatch[1];
            const restText = quoteMatch[2] ? quoteMatch[2].trim() : '';
            
            listItems.push(
              <li key={`item-${number}-heading-quote`} className="mb-1">
                <strong>{cleanedTitle}</strong>: '{quotedText}'{restText && ` ${restText}`}
              </li>
            );
          } else {
            listItems.push(
              <li key={`item-${number}-heading`} className="mb-1">
                <strong>{cleanedTitle}</strong>: {processInlineFormatting(content)}
              </li>
            );
          }
        }
        
        return hasMatches;
      };
      
      // Вариант 2: Простой нумерованный список с обработкой первого слова как заголовка
      const tryFirstWordFormat = () => {
        if (hasMatches) return true; // Уже нашли матчи
        
        // Включаем опциональную группу для ** после номера
        const simpleItemWithFirstWordRegex = /(\d+)\.\s+(?:\*\*\s*)?([A-ZА-ЯЁ][a-zA-ZА-ЯЁа-яё]+)\s+(.+?)(?=\s+\d+\.\s+|$)/gs;
        let match;
        simpleItemWithFirstWordRegex.lastIndex = 0;
        
        while ((match = simpleItemWithFirstWordRegex.exec(cleanedText)) !== null) {
          hasMatches = true;
          const number = match[1];
          const firstWord = match[2].trim();
          let restContent = match[3].trim();
          
          // Удаляем ** в начале содержимого, если они остались
          restContent = restContent.replace(/^\*\*\s*/, '');
          
          listItems.push(
            <li key={`item-${number}-firstword`} className="mb-1">
              <strong>{firstWord}</strong> {processInlineFormatting(restContent)}
            </li>
          );
        }
        
        return hasMatches;
      };
      
      // Вариант 3: Простые нумерованные пункты без выделения
      const trySimpleFormat = () => {
        if (hasMatches) return true; // Уже нашли матчи
        
        // Включаем опциональную группу для ** после номера
        const simpleItemRegex = /(\d+)\.\s+(?:\*\*\s*)?(.+?)(?=\s+\d+\.\s+|$)/gs;
        let match;
        simpleItemRegex.lastIndex = 0;
        
        while ((match = simpleItemRegex.exec(cleanedText)) !== null) {
          hasMatches = true;
          const number = match[1];
          let content = match[2].trim();
          
          // Удаляем ** в начале содержимого, если они есть
          content = content.replace(/^\*\*\s*/, '');
          
          listItems.push(
            <li key={`item-${number}-simple`} className="mb-1">
              {processInlineFormatting(content)}
            </li>
          );
        }
        
        return hasMatches;
      };
      
      // Пробуем все форматы по порядку приоритета
      tryExactScreenshotFormat() || tryHeadingFormat() || tryFirstWordFormat() || trySimpleFormat();
      
      if (hasMatches && listItems.length > 0) {
        return <ol className="pl-5 list-decimal space-y-1">{listItems}</ol>;
      }
    }

    // --- Проверка на структурированный список с переносами строк ---
    const hasDeeplyStructuredList = /^\d+\.\s+\*\*.*?\*\*\s*:/.test(text);
    if (!hasDeeplyStructuredList) {
      // --- Стандартная обработка по строкам ---
      const lines = text.split('\n');
      const result: React.ReactNode[] = [];
      let inList = false;
      let inNumberedList = false;
      let listItems: React.ReactNode[] = [];
      let numberedListItems: React.ReactNode[] = [];

      const closeLists = (currentIndex: number) => {
        if (inList) {
          result.push(
            <ul key={`list-${currentIndex-1}`} className="pl-5 list-disc space-y-1 mb-2 text-gray-900 dark:text-gray-100">
              {listItems.map((item, i) => {
                return <li key={i}>{typeof item === 'string' ? processInlineFormatting(item) : item}</li>;
              })}
            </ul>
          );
          inList = false;
          listItems = [];
        }
        if (inNumberedList) {
           result.push(
            <ol key={`numbered-list-${currentIndex-1}`} className="pl-5 list-decimal space-y-1 mb-2 text-gray-900 dark:text-gray-100">
              {numberedListItems.map((item, i) => {
                return <li key={i}>{typeof item === 'string' ? processInlineFormatting(item) : item}</li>;
              })}
            </ol>
          );
          inNumberedList = false;
          numberedListItems = [];
        }
      };

      lines.forEach((line, index) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) {
          if (inList || inNumberedList) {
            closeLists(index);
          } else if (index < lines.length - 1 && result.length > 0) {
            // Добавляем <br> для пустых строк между контентом
            const lastElement = result[result.length - 1] as React.ReactElement;
            if (!lastElement || !lastElement.key || !(typeof lastElement.key === 'string' && lastElement.key.startsWith('br-'))) {
              result.push(<br key={`br-${index}`} />);
            }
          }
          return;
        }

        // Обработка маркированных списков
        const bulletMatch = /^[-*•]\s+(.*)/.exec(trimmedLine);
        if (bulletMatch) {
          if (inNumberedList) closeLists(index);
          inList = true;
          
          const itemContent = bulletMatch[1];
          
          // Случай 1: Отношения:** Раздражительность из-за стресса...
          const colonStarPattern = /^([^:]+):\*\*\s+(.+)$/;
          const colonStarMatch = colonStarPattern.exec(itemContent);
          
          if (colonStarMatch) {
            const title = colonStarMatch[1].trim();
            const content = colonStarMatch[2].trim();
            
            listItems.push(
              <React.Fragment key={`item-colon-star-${index}`}>
                <strong>{title}:</strong> {processInlineFormatting(content)}
              </React.Fragment>
            );
            return;
          }
          
          // Случай 2: **Заголовок:** Содержание
          const boldTitlePattern = /^\*\*([^:*]+)\*\*:\s+(.+)$/;
          const boldTitleMatch = boldTitlePattern.exec(itemContent);
          
          if (boldTitleMatch) {
            const title = boldTitleMatch[1].trim();
            const content = boldTitleMatch[2].trim();
            
            listItems.push(
              <React.Fragment key={`item-bold-title-${index}`}>
                <strong>{title}:</strong> {processInlineFormatting(content)}
              </React.Fragment>
            );
            return;
          }
          
          // Обычный элемент списка
          listItems.push(processInlineFormatting(itemContent));
          return;
        }
          
        // Обработка нумерованных списков
        const numberMatch = /^(\d+)[\.\)]\s+(.*)/.exec(trimmedLine);
        if (numberMatch) {
          if (inList) closeLists(index);
          inNumberedList = true;
          
          const itemContent = numberMatch[2];
          
          // Случай 1: Отношения:** Раздражительность из-за стресса...
          const colonStarPattern = /^([^:]+):\*\*\s+(.+)$/;
          const colonStarMatch = colonStarPattern.exec(itemContent);
          
          if (colonStarMatch) {
            const title = colonStarMatch[1].trim();
            const content = colonStarMatch[2].trim();
            
            numberedListItems.push(
              <React.Fragment key={`numbered-colon-star-${index}`}>
                <strong>{title}:</strong> {processInlineFormatting(content)}
              </React.Fragment>
            );
            return;
          }
          
          // Случай 2: **Заголовок:** Содержание
          const boldTitlePattern = /^\*\*([^:*]+)\*\*:\s+(.+)$/;
          const boldTitleMatch = boldTitlePattern.exec(itemContent);
          
          if (boldTitleMatch) {
            const title = boldTitleMatch[1].trim();
            const content = boldTitleMatch[2].trim();
            
            numberedListItems.push(
              <React.Fragment key={`numbered-bold-title-${index}`}>
                <strong>{title}:</strong> {processInlineFormatting(content)}
              </React.Fragment>
            );
            return;
          }
          
          // Обычный элемент списка
          numberedListItems.push(processInlineFormatting(itemContent));
          return;
        }

        // Обычный текст
        closeLists(index);
        
        // Случай 1: Отношения:** Раздражительность из-за стресса...
        const colonStarPattern = /^([^:]+):\*\*\s+(.+)$/;
        const colonStarMatch = colonStarPattern.exec(trimmedLine);
        
        if (colonStarMatch) {
          const title = colonStarMatch[1].trim();
          const content = colonStarMatch[2].trim();
          
          result.push(
            <div key={`line-colon-star-${index}`} className="text-gray-900 dark:text-gray-100">
              <strong>{title}:</strong> {processInlineFormatting(content)}
            </div>
          );
          return;
        }
        
        // Случай 2: **Заголовок:** Содержание
        const boldTitlePattern = /^\*\*([^:*]+)\*\*:\s+(.+)$/;
        const boldTitleMatch = boldTitlePattern.exec(trimmedLine);
        
        if (boldTitleMatch) {
          const title = boldTitleMatch[1].trim();
          const content = boldTitleMatch[2].trim();
          
          result.push(
            <div key={`line-bold-title-${index}`} className="text-gray-900 dark:text-gray-100">
              <strong>{title}:</strong> {processInlineFormatting(content)}
            </div>
          );
          return;
        }
        
        // Обычный текст
        result.push(<div key={`line-${index}`} className="text-gray-900 dark:text-gray-100">{processInlineFormatting(trimmedLine)}</div>);
      });

      closeLists(lines.length); // Закрываем списки в конце
      return <div className="whitespace-pre-line text-gray-900 dark:text-gray-100">{result}</div>;
    }
    
    // Специальная обработка для сложных структурированных списков
    const structuredListItems: React.ReactNode[] = [];
    const lines = text.split('\n');
    
    // Регулярное выражение для поиска структуры "1. **Заголовок:** Текст"
    const structuredItemRegex = /^(\d+)\.\s+\*\*(.*?)\*\*\s*:\s*(.*)$/;
    
    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;
      
      const match = structuredItemRegex.exec(trimmedLine);
      if (match) {
        const title = match[2];
        const content = match[3];
        
        structuredListItems.push(
          <li key={`structured-${index}`} className="mb-1">
            <strong>{title}</strong>: {processInlineFormatting(content)}
          </li>
        );
      }
    });
    
    if (structuredListItems.length > 0) {
      return <ol className="pl-5 list-decimal space-y-1 text-gray-900 dark:text-gray-100">{structuredListItems}</ol>;
    }
    
    // Если все методы не сработали, возвращаем простой текст
    return <div className="whitespace-pre-line text-gray-900 dark:text-gray-100">{processInlineFormatting(text)}</div>;
  };
  
  if (typeof value === 'string') {
    return formatText(value);
  }
  
  if (Array.isArray(value)) {
    // Render simple list of strings or specific item structures
    if (value.length === 0) return <span className="text-gray-500 italic">Нет данных</span>;
    return (
      <ul className="pl-5 list-disc space-y-1 text-gray-900 dark:text-gray-100">
        {value.map((item, index) => {
          return (
            <li key={index}>
              {typeof item === 'string' ? processInlineFormatting(item) :
               (item.emotion ? <><span className="font-semibold">{item.emotion}:</span> {item.impact}</> :
                item.quote ? <><span className="font-semibold">«{item.quote}»</span> ({item.source || 'не указан'})</> :
                item.obstacle ? <><span className="font-semibold">{item.obstacle} ({item.type || '-'}):</span> {item.details}</> :
                item.solution ? <><span className="font-semibold">{item.solution}:</span> {item.details || item.liked_aspects}</> :
                item.myth ? <><span className="font-semibold">{item.myth}:</span> {item.details}</> :
                <span>{JSON.stringify(item)}</span> // Fallback for unknown item structure
               )}
            </li>
          );
        })}
      </ul>
    );
  }
  
  // If it's an object but not handled above (shouldn't happen often with new structure)
  if (typeof value === 'object') {
    // Проверка на объект с эмоцией и влиянием
    if (value.emotion && value.impact) {
      return (
        <div className="text-gray-900 dark:text-gray-100">
          <span className="font-semibold">{value.emotion}:</span> {value.impact}
        </div>
      );
    }
    
    // Проверка на объект с препятствием, типом и деталями
    if (value.obstacle) {
      return (
        <div className="text-gray-900 dark:text-gray-100">
          <span className="font-semibold">{value.obstacle} {value.type ? `(${value.type})` : ''}:</span> {value.details}
        </div>
      );
    }
    
    // Проверка на объект с мифом и деталями
    if (value.myth && value.details) {
      return (
        <div className="text-gray-900 dark:text-gray-100">
          <span className="font-semibold">{value.myth}:</span> {value.details}
        </div>
      );
    }
    
    // Проверка на объект с решением и деталями
    if (value.solution && value.details) {
      return (
        <div className="text-gray-900 dark:text-gray-100">
          <span className="font-semibold">{value.solution}:</span> {processInlineFormatting(value.details)}
        </div>
      );
    }
    
    // Проверка на объект с цитатой и источником
    if (value.quote) {
      return (
        <div className="text-gray-900 dark:text-gray-100">
          <span className="font-semibold">«{value.quote}»</span> {value.source ? `(${value.source})` : ''}
        </div>
      );
    }
    
    // Проверка на объект с liked_aspects (что понравилось в прошлом)
    if (value.liked_aspects) {
      return (
        <div className="text-gray-900 dark:text-gray-100">
          <span className="font-semibold">{value.solution || 'Понравилось'}:</span> {value.liked_aspects}
        </div>
      );
    }
    
    // Проверка на объект с market_objections (для top_5_market_objections)
    if (value.market_objections && Array.isArray(value.market_objections)) {
      return (
        <div className="text-gray-900 dark:text-gray-100">
          {value.description && (
            <p className="mb-3 text-gray-600 dark:text-gray-400 italic">{value.description}</p>
          )}
          <ul className="pl-5 list-disc space-y-2 text-gray-900 dark:text-gray-100">
            {value.market_objections.map((objection: string, index: number) => (
              <li key={index}>
                {processInlineFormatting(objection)}
              </li>
            ))}
          </ul>
        </div>
      );
    }
    
    // Проверка на объект с определенной структурой (description + items)
    if (value.description && Array.isArray(value.items)) {
      return (
        <div className="text-gray-900 dark:text-gray-100">
          {value.description && (
            typeof value.description === 'string' ? 
              formatText(value.description) : 
              <p className="mb-3">{JSON.stringify(value.description)}</p>
          )}
          {value.items && Array.isArray(value.items) && value.items.length > 0 && (
            <ul className="pl-5 list-disc space-y-2 text-gray-900 dark:text-gray-100">
              {value.items.map((item: any, index: number) => (
                <li key={index}>
                  {typeof item === 'string' ? 
                    processInlineFormatting(item) : 
                    item.emotion && item.impact ?
                      <><span className="font-semibold">{item.emotion}:</span> {processInlineFormatting(item.impact)}</> :
                    item.obstacle ?
                      <><span className="font-semibold">{item.obstacle} {item.type ? `(${item.type})` : ''}:</span> {processInlineFormatting(item.details)}</> :
                    item.myth && item.details ?
                      <><span className="font-semibold">{item.myth}:</span> {processInlineFormatting(item.details)}</> :
                    item.solution && item.details ?
                      <><span className="font-semibold">{item.solution}:</span> {processInlineFormatting(item.details)}</> :
                    item.quote ?
                      <><span className="font-semibold">«{item.quote}»</span> {item.source ? `(${item.source})` : ''}</> :
                    item.liked_aspects ?
                      <><span className="font-semibold">{item.solution || 'Понравилось'}:</span> {processInlineFormatting(item.liked_aspects)}</> :
                      <span>{JSON.stringify(item)}</span>
                  }
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }

    try {
      // Для других типов объектов используем JSON
      // --- Add logging here ---
      console.log("Fallback: Stringifying object in renderFinalValue:", JSON.stringify(value, null, 2));
      // ------------------------
      const jsonString = JSON.stringify(value, null, 2);
      return <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-auto max-h-60 bg-gray-50 dark:bg-gray-800 p-2 rounded">{jsonString}</pre>;
    } catch (e) {
      console.warn("Ошибка при конвертации объекта в JSON:", e);
      return <span className="text-gray-700 dark:text-gray-300">{String(value)}</span>;
    }
  }

  // Fallback for numbers, booleans etc.
  return <span>{String(value)}</span>;
};

// Recursive component to render fields based on definition
interface RenderFieldsProps {
  fields: FieldDefinition[];
  data: any;
}

const RenderFields: React.FC<RenderFieldsProps> = ({ fields, data }) => {
  // Filter out fields that don't have data
  const fieldsWithData = fields.filter(field => {
    const value = data && data[field.key] !== undefined ? data[field.key] : null;
    return value !== null && value !== undefined && value !== '';
  });

  if (fieldsWithData.length === 0) {
    return <p className="text-gray-500 italic">Нет данных</p>;
  }

  // Находим заголовки первого уровня с вложенными полями
  const headersCount = fieldsWithData.filter(field => field.fields).length;

  return (
    <div className="space-y-4">
      {fieldsWithData.map((field, index) => {
        const value = data && data[field.key] !== undefined ? data[field.key] : null;
        
        // Проверим, есть ли у текущего поля вложенные поля (это заголовок)
        const isHeader = Boolean(field.fields);
        
        // Нужна ли разделительная линия над заголовком
        // Добавляем линию если это заголовок с вложенными полями И не первый заголовок
        const isFirstHeader = isHeader && !fieldsWithData.slice(0, index).some(f => Boolean(f.fields));
        const needDividerBefore = isHeader && !isFirstHeader && headersCount > 1;
        
        if (value === null || value === undefined || value === '') {
           return null;
        }
              
        return (
          <div key={field.key} className={field.fields ? "mb-6" : "mb-4"}>
            {/* Добавляем разделительную линию перед заголовком, если нужно */}
            {needDividerBefore && (
              <div className="border-b border-gray-200 dark:border-gray-700 mb-8"></div>
            )}
            
            {/* Проверяем, имеет ли поле вложенные поля - если да, это основной заголовок */}
            {field.fields ? (
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">{field.title}</h3>
            ) : (
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{field.title}</h4>
            )}
            <div className="pl-4">
              {field.fields ? (
                // Render nested fields if defined
                <RenderFields fields={field.fields} data={value} />
              ) : (
                // Render the final value
                renderFinalValue(value)
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};


// Component for a single collapsible subsection within a part
interface SubSectionProps {
  title: string;
  data: any;
  fields?: FieldDefinition[]; // Make fields optional for direct rendering
  sectionKey: string;
  icon: React.ReactNode;
  isActive: boolean;
  onToggle: (key: string) => void;
    }

const SubSection: React.FC<SubSectionProps> = ({ title, data, fields, sectionKey, icon, isActive, onToggle }) => {
  // Basic check if data exists (might need refinement based on expected data types)
  const hasData = data !== null && data !== undefined && (
    typeof data !== 'object' || 
    (Array.isArray(data) && data.length > 0) || 
    (!Array.isArray(data) && Object.keys(data).length > 0)
  );


  if (!hasData || !title) {
    // Optionally show the title but indicate no data, or hide entirely
    // return (
    //   <div className="border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm overflow-hidden opacity-50">
    //     <div className="w-full flex items-center justify-between p-4 text-left bg-gray-100 dark:bg-gray-700">
    //       <div className="flex items-center">
    //         {icon}
    //         <span className="font-medium text-gray-600 dark:text-gray-400">{title}</span>
    //       </div>
    //       <span className="text-sm text-gray-500 italic">Нет данных</span>
    //     </div>
    //   </div>
    // );
    return null;
  }
    
    return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm overflow-hidden">
              <button
                className={`w-full flex items-center justify-between p-4 text-left transition-colors ${
          isActive ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
        onClick={() => onToggle(sectionKey)}
        aria-expanded={isActive}
        aria-controls={sectionKey + '-content'}
              >
                <div className="flex items-center">
          {icon}
          <span className="font-semibold text-lg text-gray-900 dark:text-white">{title}</span>
                </div>
                  <div className="text-blue-600 dark:text-blue-400">
                    {isActive ? <ChevronUp size={18} className="text-blue-600 dark:text-blue-400" /> : <ChevronDown size={18} className="text-blue-600 dark:text-blue-400" />}
                  </div>
              </button>
              
      {isActive && (
        <div id={sectionKey + '-content'} className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          {/* Render description if it exists */}
          {data?.description && typeof data.description === 'string' && (
             <p className="text-sm text-gray-600 dark:text-gray-400 italic mb-4">{data.description}</p>
          )}

          {/* Use RenderFields if fields definition provided, otherwise render directly */}
          {fields ? (
            <RenderFields fields={fields} data={data} />
          ) : (
            renderFinalValue(data) // Render simple value if no specific fields defined
          )}
        </div>
      )}
            </div>
          );
};

// --- Type Definitions for Structure ---

interface FieldDefinition {
  key: string;
  title: string;
  fields?: FieldDefinition[]; // For nested structures
}

interface SubSectionDefinition {
  key: string;
  title: string;
  icon: React.ReactNode;
  fields?: FieldDefinition[]; // Definition of inner fields
}

interface PartDefinition {
  index: number;
  title: string;
  icon: React.ReactNode;
  data: any; // Root data for this part
  keyPrefix: string;
  subSections: SubSectionDefinition[];
}


// --- Main Component ---

export const AudienceAnalysisResultComponent: React.FC<AudienceAnalysisResultProps> = ({ data }) => {
  const [activeParts, setActiveParts] = useState<number[]>([]);
  const [activeSubSections, setActiveSubSections] = useState<string[]>([]);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  
  // Toggle visibility of a main part (1-5)
  const togglePart = (partIndex: number) => {
    setActiveParts(prev => 
      prev.includes(partIndex) ? prev.filter(p => p !== partIndex) : [...prev, partIndex]
    );
  };
  
  // Toggle visibility of a subsection (e.g., 'part1_demographics')
  const toggleSubSection = (sectionName: string) => {
    setActiveSubSections(prev => 
      prev.includes(sectionName) ? prev.filter(s => s !== sectionName) : [...prev, sectionName]
    );
  };

  // Generate PDF function
  const generatePDF = async () => {
    setIsGeneratingPdf(true);
    setPdfError(null);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      // Ensure cyrillic font is loaded
      const fontReady = await loadCyrillicFont(pdf);
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - 2 * margin;
      let yPosition = margin;

      // Helper function to check if we need a new page
      const checkNewPage = (requiredSpace: number = 10) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          drawWatermark();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Watermark helper
      const drawWatermark = () => {
        const watermarkText = 'Dissai.io';
        pdf.setFont(fontReady ? 'TikTokSans' : 'helvetica', 'bold' as any);
        pdf.setFontSize(52);
        pdf.setTextColor(225, 225, 225);
        pdf.text(watermarkText, pageWidth / 2, pageHeight / 2, { angle: -45, align: 'center' });
        // Restore
        pdf.setTextColor(0, 0, 0); // Restore text color for content
      };

      // Helper function to add text with word wrap
      const addText = (text: string, fontSize: number, fontStyle: string, color: number[] = [0, 0, 0], indent: number = 0) => {
        pdf.setFontSize(fontSize);
        pdf.setFont(fontReady ? 'TikTokSans' : 'helvetica', fontStyle as any);
        pdf.setTextColor(color[0], color[1], color[2]);
        
        const lines = pdf.splitTextToSize(text, maxWidth - indent);
        lines.forEach((line: string) => {
          checkNewPage();
          pdf.text(line, margin + indent, yPosition);
          yPosition += fontSize * 0.5;
        });
        yPosition += 3;
      };

      // Helper function to clean markdown formatting
      const cleanText = (text: string): string => {
        if (!text) return '';
        return text
          .replace(/\*\*\s*/g, '')
          .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
          .replace(/['"«»]/g, '"')
          .trim();
      };

      // Helper function to process value for PDF
      const processValue = (value: any, indent: number = 0): void => {
        if (!value || value === null || value === undefined || value === '') return;

        if (typeof value === 'string') {
          const cleanedText = cleanText(value);
          
          // Check for numbered lists
          if (/^\d+\.\s+/.test(cleanedText)) {
            const items = cleanedText.split(/\d+\.\s+/).filter(item => item.trim());
            items.forEach((item, index) => {
              checkNewPage(15);
              addText(`${index + 1}. ${item.trim()}`, 10, 'normal', [40, 40, 40], indent);
            });
          } else {
            // Regular text
            const lines = cleanedText.split('\n').filter(line => line.trim());
            lines.forEach(line => {
              const trimmedLine = line.trim();
              
              // Check for bullet points
              if (/^[-*•]\s+/.test(trimmedLine)) {
                const content = trimmedLine.replace(/^[-*•]\s+/, '');
                checkNewPage(12);
                addText(`• ${content}`, 10, 'normal', [40, 40, 40], indent);
              } else if (trimmedLine) {
                checkNewPage(12);
                addText(trimmedLine, 10, 'normal', [40, 40, 40], indent);
              }
            });
          }
        } else if (Array.isArray(value)) {
          value.forEach((item) => {
            checkNewPage(12);
            if (typeof item === 'string') {
              addText(`• ${cleanText(item)}`, 10, 'normal', [40, 40, 40], indent);
            } else if (item.emotion && item.impact) {
              addText(`• ${item.emotion}: ${cleanText(item.impact)}`, 10, 'normal', [40, 40, 40], indent);
            } else if (item.quote && item.source) {
              addText(`• "${item.quote}" (${item.source})`, 10, 'normal', [40, 40, 40], indent);
            } else if (item.obstacle) {
              addText(`• ${item.obstacle}${item.type ? ` (${item.type})` : ''}: ${cleanText(item.details)}`, 10, 'normal', [40, 40, 40], indent);
            } else if (item.solution) {
              addText(`• ${item.solution}: ${cleanText(item.details || item.liked_aspects || '')}`, 10, 'normal', [40, 40, 40], indent);
            } else if (item.myth) {
              addText(`• ${item.myth}: ${cleanText(item.details)}`, 10, 'normal', [40, 40, 40], indent);
            }
          });
        } else if (typeof value === 'object') {
          if (value.description && Array.isArray(value.items)) {
            if (value.description) {
              checkNewPage(12);
              addText(cleanText(value.description), 10, 'italic', [80, 80, 80], indent);
              yPosition += 2;
            }
            if (value.items) {
              processValue(value.items, indent);
            }
          } else if (value.market_objections && Array.isArray(value.market_objections)) {
            if (value.description) {
              checkNewPage(12);
              addText(cleanText(value.description), 10, 'italic', [80, 80, 80], indent);
              yPosition += 2;
            }
            processValue(value.market_objections, indent);
          } else if (value.emotion && value.impact) {
            checkNewPage(12);
            addText(`${value.emotion}: ${cleanText(value.impact)}`, 10, 'normal', [40, 40, 40], indent);
          } else if (value.quote) {
            checkNewPage(12);
            addText(`"${value.quote}"${value.source ? ` (${value.source})` : ''}`, 10, 'normal', [40, 40, 40], indent);
          }
        }
      };

      // Helper function to process fields
      const processFields = (fields: FieldDefinition[], fieldData: any, indent: number = 0) => {
        fields.forEach(field => {
          const value = fieldData?.[field.key];
          if (!value || value === null || value === undefined || value === '') return;

          checkNewPage(15);
          
          // Add field title
          if (field.fields) {
            // Main header
            addText(field.title, 12, 'bold', [30, 30, 30], indent);
            yPosition += 2;
            // Process nested fields
            if (typeof value === 'object') {
              processFields(field.fields, value, indent + 5);
            }
          } else {
            // Subheader
            addText(field.title, 11, 'bold', [40, 40, 40], indent);
            yPosition += 1;
            // Process value
            processValue(value, indent + 5);
            yPosition += 3;
          }
        });
      };

      // Title
      pdf.setFontSize(18);
      pdf.setFont(fontReady ? 'TikTokSans' : 'helvetica', 'bold' as any);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Анализ целевой аудитории', margin, yPosition);
      yPosition += 12;

      // Add watermark to the first page
      drawWatermark();

      // Description
      if (audience_analysis.description) {
        checkNewPage(15);
        addText(audience_analysis.description, 11, 'italic', [60, 60, 60]);
        yPosition += 5;
      }

      // Process each part
      partsDefinition.forEach((part) => {
        const hasPartData = part.data && typeof part.data === 'object' && Object.keys(part.data).length > 1;
        if (!hasPartData) return;

        // Add new page for each part
        pdf.addPage();
        drawWatermark();
        yPosition = margin;

        // Part title
        addText(part.title, 14, 'bold', [0, 80, 180]);
        yPosition += 3;

        // Part description
        if (part.data.description) {
          addText(part.data.description, 10, 'italic', [80, 80, 80]);
          yPosition += 5;
        }

        // Process subsections
        part.subSections.forEach((subSection) => {
          const subSectionData = part.data[subSection.key];
          if (!subSectionData) return;

          checkNewPage(20);

          // Subsection title
          addText(subSection.title, 13, 'bold', [20, 20, 20]);
          yPosition += 2;

          if (subSection.fields) {
            processFields(subSection.fields, subSectionData, 5);
          } else {
            processValue(subSectionData, 5);
          }
          
          yPosition += 5;
        });
      });

      // Save PDF
      pdf.save('Анализ_целевой_аудитории.pdf');
    } catch (error) {
      console.error('Ошибка при генерации PDF:', error);
      setPdfError('Произошла ошибка при создании PDF. Пожалуйста, попробуйте снова.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // --- Data Check ---
  if (!data || !data.audience_analysis) {
    return (
      <div className="p-8 text-center">
        <p className="text-xl text-gray-600 dark:text-gray-400">Данные анализа отсутствуют</p>
      </div>
    );
  }
  
  const { audience_analysis } = data;
  
  // --- Parts Definition ---
  // Defines the structure, titles, icons, and data mapping for the entire analysis
  const partsDefinition: PartDefinition[] = [
    { 
      index: 1, 
      title: "Часть 1: Образ жизни и доход", 
      icon: <Users className="mr-2 text-gray-700 dark:text-white" size={20} />,
      data: audience_analysis.part_1_identity_values_worldview,
      keyPrefix: 'part1',
      subSections: [
        { 
          key: 'demographics', title: "Демография", icon: <Users size={16} className="mr-2 text-gray-700 dark:text-white" />,
          fields: [
            { key: 'age_analysis', title: "Анализ возраста", fields: [
              { key: 'influence_on_behavior', title: "Влияние на поведение" },
              { key: 'changes_last_5_10_years', title: "Изменения за 5-10 лет" },
              { key: 'life_stage_and_crises', title: "Жизненный этап и кризисы" }
            ]}
          ]
        },
        { 
          key: 'lifestyle_snapshot', title: "Образ жизни", icon: <Heart size={16} className="mr-2 text-gray-700 dark:text-white" />,
          fields: [
            { key: 'living_location', title: "Место проживания" },
            { key: 'daily_routine', title: "Распорядок дня" },
            { key: 'leisure_and_entertainment', title: "Досуг" },
            { key: 'hobbies_and_interests', title: "Хобби и интересы" },
            { key: 'tech_adoption', title: "Отношение к технологиям" },
            { key: 'life_stance', title: "Жизненная позиция" }
          ]
        },
        { 
          key: 'gender_ratio_analysis', title: "Анализ гендера", icon: <Users size={16} className="mr-2 text-gray-700 dark:text-white" />,
          fields: [
            { key: 'motivation_differences', title: "Различия в мотивации" },
            { key: 'decision_maker', title: "Кто принимает решение" },
            { key: 'gender_influence_on_perception', title: "Влияние гендера на восприятие" }
          ]
        },
        { 
          key: 'average_income_analysis', title: "Анализ дохода", icon: <Award size={16} className="mr-2 text-gray-700 dark:text-white" />,
          fields: [
            { key: 'money_perception', title: "Восприятие денег" },
            { key: 'spending_habits', title: "Траты" },
            { key: 'budget_for_solution_percentage', title: "% бюджета на решение" }
          ]
        }
      ] 
    },
    { 
      index: 2, 
      title: "Часть 2: Проблемы и фразы от окружающих", 
      icon: <AlertTriangle className="mr-2 text-gray-700 dark:text-white" size={20} />,
      data: audience_analysis.part_2_pains_fears_triggers,
      keyPrefix: 'part2',
      subSections: [
         { 
           key: 'core_problems', title: "Ключевые проблемы", icon: <AlertTriangle size={16} className="mr-2 text-gray-700 dark:text-white" />,
           fields: [
             { key: 'top_3_5_problems', title: "Топ 3-5 проблем" },
             { key: 'triggering_events', title: "Триггерные события" },
             { key: 'ignorance_period', title: "Период игнорирования" },
             { key: 'failed_attempts_to_solve', title: "Неудачные попытки решения" },
             { key: 'myths_about_solvability', title: "Мифы о (не)решаемости" },
             { key: 'unbearable_aspect', title: "Что невыносимо" }
           ]
         },
         { 
           key: 'painful_phrases_from_others', title: "Болезненные фразы от окружающих", icon: <AlertTriangle size={16} className="mr-2 text-gray-700 dark:text-white" />
           // No fields needed - renderFinalValue handles array of items with quote and source
         }
      ]
    },
    {
      index: 3,
      title: "Часть 3: Барьеры и прошлый опыт",
      icon: <Award className="mr-2 text-gray-700 dark:text-white" size={20} />,
      data: audience_analysis.part_3_barriers_fears_past_experience,
      keyPrefix: 'part3',
      subSections: [
        { 
          key: 'key_obstacles_and_myths', title: "Ключевые препятствия и мифы", icon: <Award size={16} className="mr-2 text-gray-700 dark:text-white" />,
          fields: [
             { key: 'top_3_5_obstacles', title: "Топ 3-5 препятствий" }, // Needs special handling if it's an array of objects
             { key: 'internal_barriers_to_start', title: "Внутренние барьеры (начать)" },
             { key: 'external_delay_factors', title: "Внешние факторы (откладывание)" },
             { key: 'self_doubt_and_limiting_beliefs', title: "Сомнения и ограничивающие убеждения" },
             { key: 'justification_for_inaction_phrases', title: "Фразы самооправдания" },
             { key: 'relapse_reasons', title: "Причины 'отката'" }
           ]
        },
        { 
          key: 'past_experience_with_solutions', title: "Прошлый опыт с решениями", icon: <Award size={16} className="mr-2 text-gray-700 dark:text-white" />,
          fields: [
            { key: 'what_they_tried', title: "Что пробовали" }, // Needs special handling if it's an array of objects
            { key: 'what_failed_and_thoughts', title: "Что не сработало и мысли", fields: [
              { key: 'disappointment_level', title: "Уровень разочарования" },
              { key: 'impact_on_trust', title: "Влияние на доверие" },
              { key: 'negative_self_talk', title: "Негативный внутренний диалог" },
              { key: 'attitude_change_towards_problem', title: "Изменение отношения к проблеме" },
              { key: 'blame_attribution', title: "Кого винили" }
            ]},
            { key: 'what_liked_in_past_solutions', title: "Что понравилось в прошлом" }, // Needs special handling if it's an array of objects
            { key: 'what_disliked_in_past_solutions_quotes', title: "Цитаты: что не понравилось" },
            { key: 'what_they_refuse_to_do_quotes', title: "Цитаты: что больше не будут делать" }
          ]
        }
      ]
    },
    {
      index: 4,
      title: "Часть 4: Рынок и возражения",
      icon: <Compass className="mr-2 text-gray-700 dark:text-white" size={20} />,
      data: audience_analysis.part_4_specific_of_market,
      keyPrefix: 'part4',
      subSections: [
        { 
          key: 'market_definition_of_success', title: "Определение успеха на рынке", icon: <BarChart size={16} className="mr-2 text-gray-700 dark:text-white" />,
          fields: [
            { key: 'key_expected_results', title: "Ключевые ожидаемые результаты" },
            { key: 'success_indicators', title: "Индикаторы успеха" },
            { key: 'trust_signals', title: "Сигналы доверия" },
            { key: 'convincing_social_proof', title: "Убедительные соц. доказательства" },
            { key: 'authoritative_signals', title: "Авторитетные сигналы" }
          ]
        },
        { 
          key: 'top_5_market_objections', title: "Топ-5 возражений рынка", icon: <AlertTriangle size={16} className="mr-2 text-gray-700 dark:text-white" />
          // No fields needed - renderFinalValue handles object with market_objections array
        }
      ]
    },
    {
      index: 5,
      title: "Часть 5: Триггеры и оценка ценности",
      icon: <Zap className="mr-2 text-gray-700 dark:text-white" size={20} />,
      data: audience_analysis.part_5_triggers_decision_points_influence_mechanics,
      keyPrefix: 'part5',
      subSections: [
        { 
          key: 'key_decision_triggers', title: "Ключевые триггеры решения", icon: <Target size={16} className="mr-2 text-gray-700 dark:text-white" />,
          fields: [
            { key: 'final_straw_situations', title: "Ситуации 'последней капли'" },
            { key: 'strongest_purchase_emotions', title: "Сильнейшие эмоции покупки" },
            { key: 'aha_moment', title: "Момент 'Aha!'" },
            { key: 'segment_specific_triggers', title: "Специфичные триггеры сегментов" }
          ]
        },
        { 
          key: 'value_perception_evaluation', title: "Восприятие и оценка ценности", icon: <Lightbulb size={16} className="mr-2 text-gray-700 dark:text-white" />,
          fields: [
            { key: 'definition_of_worth_it', title: "Критерии 'стоит того'" },
            { key: 'main_quality_indicators', title: "Главные индикаторы качества" },
            { key: 'non_obvious_value_enhancers', title: "Неочевидные усилители ценности" },
            { key: 'desire_boosting_extras', title: "Дополнения, усиливающие желание" },
            { key: 'must_have_offer_phrases', title: "Обязательные фразы в оффере" }
          ]
        },
      ]
    }
  ];

  // --- Render Logic ---
  return (
    <div className="space-y-8">
      
      {/* Root Description and PDF Button */}
      <div className="mt-10 mb-6">
        <div className="flex flex-row items-center justify-between gap-4 mb-4">
          <h2 className="text-black dark:text-white font-semibold text-xl">Анализ целевой аудитории</h2>
          <button
            onClick={generatePDF}
            disabled={isGeneratingPdf}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all border ${
              isGeneratingPdf
                ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                : 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
            }`}
          >
            <Download size={18} />
            {isGeneratingPdf ? (
              <>
                <span className="hidden sm:inline">Генерация</span>
                <span className="sm:ml-1">PDF...</span>
              </>
            ) : (
              <>
                <span className="hidden sm:inline">Скачать</span>
                <span className="sm:ml-1">PDF</span>
              </>
            )}
          </button>
        </div>

        {pdfError && (
          <div className="mb-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            {pdfError}
          </div>
        )}
        
        {audience_analysis.description && (
          <div className="mt-4">
            <h3 className="text-black dark:text-white font-semibold mb-2">Описание:</h3>
            <p className="text-gray-700 dark:text-gray-300 italic">
              {audience_analysis.description}
            </p>
          </div>
        )}
      </div>
      
      {/* Main Content Area - Loop through parts */}
      <div className="space-y-4">
        {partsDefinition.map((part) => {
          // Basic check if the part data exists and is not empty
          const hasPartData = part.data && typeof part.data === 'object' && Object.keys(part.data).length > 1; // Greater than 1 to exclude if only description exists
          if (!hasPartData) return null; // Don't render the part if no data besides description
          
          return (
            <div key={part.index} className="border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden">
              {/* Part Header Button */}
          <button
            className={`w-full flex items-center justify-between p-5 text-left transition-colors ${
                  activeParts.includes(part.index) ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50'
            }`}
                onClick={() => togglePart(part.index)}
                aria-expanded={activeParts.includes(part.index)}
                aria-controls={`part-${part.index}-content`}
          >
            <div className="flex items-center">
                  {part.icon}
                  <span className="font-bold text-lg md:text-xl text-gray-900 dark:text-white">{part.title}</span>
            </div>
            <div className="text-blue-600 dark:text-blue-400">
                  {activeParts.includes(part.index) ? <ChevronUp size={20} className="text-blue-600 dark:text-blue-400" /> : <ChevronDown size={20} className="text-blue-600 dark:text-blue-400" />}
            </div>
          </button>
          
              {/* Part Content (visible when active) */}
              {activeParts.includes(part.index) && (
                <div id={`part-${part.index}-content`} className="p-5 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                  {/* Part Description and SubSection Toggles */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
                    {part.data.description && (
                      <p className="text-gray-700 dark:text-gray-300 flex-grow pr-4 italic">
                        {part.data.description}
                      </p>
          )}
        </div>
        
                  {/* Render Subsections */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                     {part.subSections.map((subSection) => {
                       const sectionKey = `${part.keyPrefix}_${subSection.key}`;
                       // Access data using bracket notation for dynamic keys
                       const subSectionData = (part.data as any)[subSection.key]; 
                       return (
                         <SubSection
                           key={sectionKey}
                           title={subSection.title}
                           data={subSectionData}
                           fields={subSection.fields} // Pass the fields definition
                           sectionKey={sectionKey}
                           icon={subSection.icon}
                           isActive={activeSubSections.includes(sectionKey)}
                           onToggle={toggleSubSection}
                         />
                       );
                     })}
            </div>
            </div>
          )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AudienceAnalysisResultComponent; 